import React, {FC, useState, useEffect} from 'react'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {isNotEmpty, toAbsoluteUrl, QUERIES} from '../../../../../_dashboard/helpers'
import { Tour} from '../core/_models'
import clsx from 'clsx'
import {useListView} from '../core/ListViewProvider'
import {DataLoading} from '../../../../../_dashboard/partials/components/loading/DataLoading'
import {useQueryResponse} from '../core/QueryResponseProvider'
import { createOne, updateOne } from '../../../../services/common_requests'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { Modal } from 'react-bootstrap'
import { convertDurationDisplayValue } from '../../utils/formatTourDuration'

type Props = {
  isTourLoading: boolean
  tour: Tour
}

const editTourSchema = Yup.object().shape({
  title: Yup.string()
    .min(3, 'Minimum 3 symbols')
    .max(100, 'Maximum 100 symbols')
    .required('Title is required'),
  tourMinPeople: Yup.number()
    .min(1, 'Minimum 1 person')
    .required('Minimum people is required'),
  tourMaxPeople: Yup.number()
    .min(1, 'Minimum 1 person')
    .required('Maximum people is required'),
  duration: Yup.number()
    .min(1, 'Minimum 1')
    .required('Duration is required'),
  durationUnit: Yup.string().oneOf(['hours', 'days']).required(),
})

const TourEditModalForm: FC<Props> = ({tour, isTourLoading}) => {
  const {setItemIdForUpdate} = useListView()
  const {refetchData} = useQueryResponse()
  const intl = useIntl()

  const [tourForEdit] = useState<Tour>({
    ...tour,
  })

  const cancel = (withRefresh?: boolean) => {
    if (withRefresh) {
      refetchData()
    }
    setItemIdForUpdate(undefined)
  }

  const formik = useFormik({
    initialValues: {
      _id: tourForEdit._id || '',
      title: tourForEdit.title || '',
      youtubeVideo: tourForEdit.youtubeVideo || '',
      tourMinPeople: tourForEdit.tourMinPeople || 2,
      tourMaxPeople: tourForEdit.tourMaxPeople || 10,
      duration: tourForEdit.duration || 1,
      durationUnit: (tourForEdit as any).durationUnit === 'days' ? 'days' : 'hours',
      status: tourForEdit.status || 'draft',
      translations: tourForEdit.translations || [{ description: '', language: 'fr' }],
    },
    validationSchema: editTourSchema,
    onSubmit: async (values, {setSubmitting}) => {
      try {
        setSubmitting(true)
        const payload = {
          ...values,
          durationUnit: values.durationUnit === 'days' ? 'days' : 'hours',
        }
        if (isNotEmpty(values._id)) {
          await updateOne(payload, "/tours")
        } else {
          await createOne(payload, "/tours")
        }
      } catch (ex) {
        toast.error('An error occurred while saving the tour')
      } finally {
        setSubmitting(false)
        cancel(true)
      }
    },
  })

  useEffect(() => {
    if (tour) {
      const formValues = {
        _id: tour._id || '',
        title: tour.title || '',
        youtubeVideo: tour.youtubeVideo || '',
        tourMinPeople: tour.tourMinPeople || 2,
        tourMaxPeople: tour.tourMaxPeople || 10,
        duration: tour.duration || 1,
        durationUnit: (tour as any).durationUnit === 'days' ? 'days' : 'hours',
        status: tour.status || 'draft',
        translations: tour.translations || [{ description: '', language: 'fr' }],
      } as const
      formik.setValues(formValues as any)
    }
  }, [tour])

  return (
    <>
      <form id='kt_modal_add_tour_form' className='form' onSubmit={formik.handleSubmit} noValidate>
        <div
          className='d-flex flex-column scroll-y me-n7 pe-7'
          id='kt_modal_add_tour_scroll'
          data-kt-scroll='true'
          data-kt-scroll-activate='{default: false, lg: true}'
          data-kt-scroll-max-height='auto'
          data-kt-scroll-dependencies='#kt_modal_add_tour_header'
          data-kt-scroll-wrappers='#kt_modal_add_tour_scroll'
          data-kt-scroll-offset='300px'
        >
          {/* begin::Input group */}
          <div className='fv-row mb-7'>
            {/* begin::Label */}
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'TOUR.FIELDS.TITLE'})}
            </label>
            {/* end::Label */}

            {/* begin::Input */}
            <input
              placeholder={intl.formatMessage({id: 'TOUR.FIELDS.TITLE'})}
              {...formik.getFieldProps('title')}
              type='text'
              name='title'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                {'is-invalid': formik.touched.title && formik.errors.title},
                {'is-valid': formik.touched.title && !formik.errors.title}
              )}
              autoComplete='off'
              disabled={formik.isSubmitting || isTourLoading}
            />
            {formik.touched.title && formik.errors.title && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.title}</span>
                </div>
              </div>
            )}
            {/* end::Input */}
          </div>
          {/* end::Input group */}

          {/* begin::Input group */}
          <div className='fv-row mb-7'>
            {/* begin::Label */}
            <label className='fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'TOUR.FIELDS.DESCRIPTION'})}
            </label>
            {/* end::Label */}

            {/* begin::ReactQuill */}
            <ReactQuill
              value={formik.values.translations?.[0]?.description || ''}
              onChange={(value) => formik.setFieldValue('translations[0].description', value)}
              onBlur={() => formik.setFieldTouched('translations[0].description', true)}
              modules={{
                toolbar: [
                  ['bold', 'italic', 'underline', 'strike'],
                  ['blockquote'],
                  [{ header: 1 }, { header: 2 }],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                  [{ indent: '-1' }, { indent: '+1' }],
                  [{ direction: 'rtl' }],
                  [{ size: ['small', false, 'large', 'huge'] }],
                  [{ header: [1, 2, 3, 4, 5, 6, false] }],
                  [{ font: [] }],
                  ['clean'],
                ],
              }}
              formats={[
                'header',
                'font',
                'size',
                'bold',
                'italic',
                'underline',
                'strike',
                'blockquote',
                'list',
                'bullet',
                'indent',
                'link',
                'image',
                'video',
              ]}
            />
            {/* end::ReactQuill */}
          </div>
          {/* end::Input group */}

          {/* begin::Input group */}
          <div className='fv-row mb-7'>
            {/* begin::Label */}
            <label className='fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'TOUR.FIELDS.VIDEO_URL'})}
            </label>
            {/* end::Label */}

            {/* begin::Input */}
            <input
              placeholder='https://www.youtube.com/watch?v=example'
              {...formik.getFieldProps('youtubeVideo')}
              type='text'
              name='youtubeVideo'
              className='form-control form-control-solid mb-3 mb-lg-0'
              autoComplete='off'
              disabled={formik.isSubmitting || isTourLoading}
            />
            {/* end::Input */}
          </div>
          {/* end::Input group */}

          {/* begin::Input group - Duration */}
          <div className='fv-row mb-7'>
            {/* begin::Label */}
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'TOUR.FIELDS.DURATION'})}
            </label>
            {/* end::Label */}

            {/* begin::Input */}
            <div className='input-group mb-3 mb-lg-0'>
              <input
                placeholder={
                  formik.values.durationUnit === 'days'
                    ? intl.formatMessage({id: 'TOUR.PLACEHOLDERS.DURATION.DAYS'})
                    : intl.formatMessage({id: 'TOUR.PLACEHOLDERS.DURATION.HOURS'})
                }
                {...formik.getFieldProps('duration')}
                type='number'
                name='duration'
                min='1'
                className={clsx(
                  'form-control form-control-solid',
                  {'is-invalid': formik.touched.duration && formik.errors.duration},
                  {'is-valid': formik.touched.duration && !formik.errors.duration}
                )}
                autoComplete='off'
                disabled={formik.isSubmitting || isTourLoading}
              />
              <select
                className='form-select form-select-solid'
                style={{maxWidth: '9rem'}}
                name='durationUnit'
                value={formik.values.durationUnit || 'hours'}
                disabled={formik.isSubmitting || isTourLoading}
                onChange={(e) => {
                  const nextUnit = e.target.value === 'days' ? 'days' : 'hours'
                  const prevUnit = formik.values.durationUnit === 'days' ? 'days' : 'hours'
                  const current = Number(formik.values.duration)
                  if (prevUnit !== nextUnit && Number.isFinite(current) && current > 0) {
                    formik.setFieldValue(
                      'duration',
                      convertDurationDisplayValue(current, prevUnit, nextUnit),
                    )
                  }
                  formik.setFieldValue('durationUnit', nextUnit)
                }}
              >
                <option value='days'>
                  {intl.formatMessage({id: 'TOUR.PLACEHOLDERS.DURATION.DAYS'})}
                </option>
                <option value='hours'>
                  {intl.formatMessage({id: 'TOUR.PLACEHOLDERS.DURATION.HOURS'})}
                </option>
              </select>
            </div>
            {formik.touched.duration && formik.errors.duration && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.duration}</span>
                </div>
              </div>
            )}
            {/* end::Input */}
          </div>
          {/* end::Input group */}

          {/* begin::Input group - Min People */}
          <div className='fv-row mb-7'>
            {/* begin::Label */}
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'TOUR.FIELDS.MIN_PEOPLE'})}
            </label>
            {/* end::Label */}

            {/* begin::Input */}
            <input
              placeholder='2'
              {...formik.getFieldProps('tourMinPeople')}
              type='number'
              name='tourMinPeople'
              min='1'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                {'is-invalid': formik.touched.tourMinPeople && formik.errors.tourMinPeople},
                {'is-valid': formik.touched.tourMinPeople && !formik.errors.tourMinPeople}
              )}
              autoComplete='off'
              disabled={formik.isSubmitting || isTourLoading}
            />
            {formik.touched.tourMinPeople && formik.errors.tourMinPeople && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.tourMinPeople}</span>
                </div>
              </div>
            )}
            {/* end::Input */}
          </div>
          {/* end::Input group */}

          {/* begin::Input group - Max People */}
          <div className='fv-row mb-7'>
            {/* begin::Label */}
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'TOUR.FIELDS.MAX_PEOPLE'})}
            </label>
            {/* end::Label */}

            {/* begin::Input */}
            <input
              placeholder='10'
              {...formik.getFieldProps('tourMaxPeople')}
              type='number'
              name='tourMaxPeople'
              min='1'
              className={clsx(
                'form-control form-control-solid mb-3 mb-lg-0',
                {'is-invalid': formik.touched.tourMaxPeople && formik.errors.tourMaxPeople},
                {'is-valid': formik.touched.tourMaxPeople && !formik.errors.tourMaxPeople}
              )}
              autoComplete='off'
              disabled={formik.isSubmitting || isTourLoading}
            />
            {formik.touched.tourMaxPeople && formik.errors.tourMaxPeople && (
              <div className='fv-plugins-message-container'>
                <div className='fv-help-block'>
                  <span role='alert'>{formik.errors.tourMaxPeople}</span>
                </div>
              </div>
            )}
            {/* end::Input */}
          </div>
          {/* end::Input group */}

          {/* begin::Input group - Status */}
          <div className='fv-row mb-7'>
            {/* begin::Label */}
            <label className='required fw-bold fs-6 mb-2'>
              {intl.formatMessage({id: 'TOUR.FIELDS.STATUS'})}
            </label>
            {/* end::Label */}
            
            {/* begin::Statuses */}
            <div className='d-flex fv-row'>
              {/* begin::Radio */}
              <div className='form-check form-check-custom form-check-solid me-5'>
                {/* begin::Input */}
                <input
                  className='form-check-input'
                  {...formik.getFieldProps('status')}
                  name='status'
                  type='radio'
                  value='draft'
                  id='kt_modal_update_status_option_0'
                  checked={formik.values.status === 'draft'}
                  disabled={formik.isSubmitting || isTourLoading}
                />
                {/* end::Input */}
                {/* begin::Label */}
                <label className='form-check-label' htmlFor='kt_modal_update_status_option_0'>
                  {intl.formatMessage({id: 'TOUR.FIELDS.PUBLISHED_STATE_OFF'})}
                </label>
                {/* end::Label */}
              </div>
              {/* end::Radio */}

              {/* begin::Radio */}
              <div className='form-check form-check-custom form-check-solid'>
                {/* begin::Input */}
                <input
                  className='form-check-input'
                  {...formik.getFieldProps('status')}
                  name='status'
                  type='radio'
                  value='published'
                  id='kt_modal_update_status_option_1'
                  checked={formik.values.status === 'published'}
                  disabled={formik.isSubmitting || isTourLoading}
                />
                {/* end::Input */}
                {/* begin::Label */}
                <label className='form-check-label' htmlFor='kt_modal_update_status_option_1'>
                  {intl.formatMessage({id: 'TOUR.FIELDS.PUBLISHED_STATE_ON'})}
                </label>
                {/* end::Label */}
              </div>
              {/* end::Radio */}
            </div>
            {/* end::Statuses */}
          </div>
          {/* end::Input group */}
        </div>
        {/* end::Scroll */}

        {/* begin::Actions */}
        <div className='text-center pt-15'>
          <button
            type='reset'
            onClick={() => cancel()}
            className='btn btn-light me-3'
            data-kt-tours-modal-action='cancel'
            disabled={formik.isSubmitting || isTourLoading}
          >
            {intl.formatMessage({id: 'ACTION.CANCEL'})}
          </button>

          <button
            type='submit'
            className='btn btn-primary'
            data-kt-tours-modal-action='submit'
            disabled={isTourLoading || formik.isSubmitting || !formik.isValid || !formik.dirty}
          >
            <span className='indicator-label'>{intl.formatMessage({id: 'ACTION.EDIT'})}</span>
            {(formik.isSubmitting || isTourLoading) && (
              <span className='indicator-progress'>
                {intl.formatMessage({id: 'COMMON.PLEASE_WAIT'})}{' '}
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        </div>
        {/* end::Actions */}
      </form>
      {(formik.isSubmitting || isTourLoading) && <DataLoading />}
    </>
  )
}

export {TourEditModalForm}
